import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { StrictMode, useEffect, useRef } from 'react'
import { createRoot, type Root } from 'react-dom/client'

import { PortalContainerContext } from '@/components/ui/portal-container'
import { CardView } from '@/features/card-preview/card-view'
import type { WidgetTemplate } from '@/features/conversation/types/message'
import css from '../styles.css?inline'
import { LOCAL_TEMPLATES, resolveCard, type Card, type ResolveOptions } from './resolve'
import { MessageView, resolveMessage, type ActionResult, type Message, type MessageAction, type ResolvedMessage } from './message'

/**
 * Pair templates render SDK — draws any widget template card in the host page, with the
 * widget's own renderers, and no network for Pair's own cards.
 *
 *   import { render } from '@pair-ai/templates-render-sdk'
 *   const card = render(element, message, { lang: 'ar' })
 *   card.update(nextMessage); card.destroy()
 *
 * or <pair-template> with `.message = …`. The card lives in a shadow root, so its styles and
 * the page's never meet.
 */

export const VERSION: string = __SDK_VERSION__

export interface RenderOptions extends ResolveOptions {
  /** light | dark (default light). */
  theme?: 'light' | 'dark'
  /** Padding around the card, px (default 0). */
  padding?: number
  /** Called with the card's height each time it changes. */
  onHeight?: (height: number) => void
  /** Called when the card cannot be resolved or drawn. */
  onError?: (error: Error) => void
  /**
   * A button on the card was tapped (Add to cart…): {type: 'action', id, data} — `id` is the action ("danah.<id>"
   * for an agent's own action), `data` what it carries (the product). Return {success, message} (or a promise of it)
   * and the card shows it; without a handler the card's buttons do nothing.
   */
  onAction?: (action: MessageAction) => Promise<ActionResult | void> | ActionResult | void
}

export interface RenderHandle {
  /** Draw another card (or the same one with new options) in the same place. */
  update(card: Card, opts?: RenderOptions): Promise<void>
  /** Unmount and free the element. */
  destroy(): void
}

// ---- styles: shadow-scoped, except what only works at document level ----
let globalsAdded = false
let shadowCss = ''

/**
 * Fonts and @property registrations do not work from inside a shadow root, so they go to the
 * document once; everything else is scoped to each card's shadow root.
 */
function splitStyles(): string {
  if (shadowCss) return shadowCss
  const global: string[] = []
  // @import "…" or @import url(…): minified CSS writes the first.
  let rest = css.replace(/@import\s*(?:url\()?\s*["'][^"']+["']\s*\)?[^;]*;/g, (m) => {
    global.push(m)
    return ''
  })
  rest = rest.replace(/@(font-face|property)\s*[^{]*\{[^}]*\}/g, (m) => {
    global.push(m)
    return ''
  })
  shadowCss = rest.replace(/:root(?![\w-])/g, ':host')
  if (!globalsAdded && typeof document !== 'undefined') {
    globalsAdded = true
    const style = document.createElement('style')
    style.setAttribute('data-pair-templates', VERSION)
    style.textContent = global.join('\n')
    document.head.appendChild(style)
  }
  return shadowCss
}

const queryClient = new QueryClient({ defaultOptions: { queries: { retry: false }, mutations: { retry: false } } })

function Card({ template, opts, portal }: { template: WidgetTemplate; opts: RenderOptions; portal: HTMLElement }) {
  const box = useRef<HTMLDivElement>(null)
  const onHeight = opts.onHeight
  useEffect(() => {
    const el = box.current
    if (!el || !onHeight) return
    const report = () => onHeight(Math.ceil(el.scrollHeight))
    const ro = new ResizeObserver(report)
    ro.observe(el)
    report()
    return () => ro.disconnect()
  }, [template, onHeight])
  return (
    <PortalContainerContext.Provider value={portal}>
      <CardView
        template={template}
        theme={opts.theme}
        boxRef={box}
        padding={opts.padding ?? 0}
        onAction={opts.onAction ? async (e) => (await opts.onAction!({ type: 'action', id: e.id, text: '', data: e.data })) ?? { success: true } : undefined}
      />
    </PortalContainerContext.Provider>
  )
}

/** A shadow root in `target` holding the styles, the React mount and the portal for dialogs. */
function mountShadow(target: HTMLElement): { shadow: ShadowRoot; mount: HTMLDivElement; portal: HTMLDivElement } {
  const shadow = target.shadowRoot ?? target.attachShadow({ mode: 'open' })
  shadow.innerHTML = ''
  const style = document.createElement('style')
  // the widget writes message text in a fixed dark colour; on the dark theme it reads light
  style.textContent = splitStyles() + '\n.pair-dark .ai-md-body{color:#F2F2F2 !important}'
  const mount = document.createElement('div')
  const portal = document.createElement('div')
  portal.setAttribute('data-pair-portal', '')
  shadow.append(style, mount, portal)
  return { shadow, mount, portal }
}

/** Draw a card into `target`. */
export function render(target: HTMLElement, card: Card, opts: RenderOptions = {}): RenderHandle {
  const { shadow, mount, portal } = mountShadow(target)
  let root: Root | null = createRoot(mount)
  let current = opts
  let seq = 0

  const draw = async (next: Card, o: RenderOptions) => {
    current = o
    const mine = ++seq
    try {
      const template = await resolveCard(next, o)
      if (mine !== seq || !root) return
      root.render(
        <StrictMode>
          <QueryClientProvider client={queryClient}>
            <Card template={template} opts={o} portal={portal} />
          </QueryClientProvider>
        </StrictMode>,
      )
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err))
      if (o.onError) o.onError(e)
      else console.error('[pair-templates]', e)
      throw e
    }
  }

  const first = draw(card, opts)
  first.catch(() => {})
  return {
    update: (next, o) => draw(next, { ...current, ...o }),
    destroy: () => {
      root?.unmount()
      root = null
      shadow.innerHTML = ''
    },
  }
}

export interface MessageOptions extends RenderOptions {
  /**
   * Something under the message or on a card was tapped: a suggestion or an answer ({type, text}: send it as the
   * customer's message), or an action button ({type: 'action', id, data}: run it, e.g. through Pair's dispatch, and
   * return {success, message} for the button). Without it the options are shown, inert.
   */
  onAction?: (action: MessageAction) => Promise<ActionResult | void> | ActionResult | void
}

/**
 * A custom element's action event: its detail is the action plus `respond(result)`. A page that runs the action
 * calls event.preventDefault() and later detail.respond({success, message}); otherwise the button reads as done.
 */
function actionEvent(el: HTMLElement, name: string, action: MessageAction): Promise<ActionResult | void> {
  return new Promise((resolve) => {
    const ev = new CustomEvent(name, { detail: { ...action, respond: (r: ActionResult) => resolve(r) }, bubbles: true, composed: true, cancelable: true })
    const handled = !el.dispatchEvent(ev)
    if (!handled || action.type !== 'action') resolve(undefined)
  })
}

export interface MessageHandle {
  /** Draw another message (or the same one with new options) in the same place. */
  update(message: Message, opts?: MessageOptions): Promise<void>
  destroy(): void
}

function MessageBox({ message, opts, portal }: { message: ResolvedMessage; opts: MessageOptions; portal: HTMLElement }) {
  const box = useRef<HTMLDivElement>(null)
  const onHeight = opts.onHeight
  useEffect(() => {
    const el = box.current
    if (!el || !onHeight) return
    const report = () => onHeight(Math.ceil(el.scrollHeight))
    const ro = new ResizeObserver(report)
    ro.observe(el)
    report()
    return () => ro.disconnect()
  }, [message, onHeight])
  return (
    <PortalContainerContext.Provider value={portal}>
      <MessageView message={message} theme={opts.theme} padding={opts.padding ?? 0} boxRef={box} onAction={opts.onAction} />
    </PortalContainerContext.Provider>
  )
}

/**
 * Draw a whole reply into `target`: its pieces in order (text, card, text…) and the suggestions or
 * question under it. Takes Danah's final `parts` event data, the widget's stored message, or just
 * the pieces (see resolveMessage).
 */
export function renderMessage(target: HTMLElement, message: Message, opts: MessageOptions = {}): MessageHandle {
  const { shadow, mount, portal } = mountShadow(target)
  let root: Root | null = createRoot(mount)
  let current = opts
  let seq = 0
  const draw = async (next: Message, o: MessageOptions) => {
    current = o
    const mine = ++seq
    const resolved = await resolveMessage(next, o)
    if (mine !== seq || !root) return
    root.render(
      <StrictMode>
        <QueryClientProvider client={queryClient}>
          <MessageBox message={resolved} opts={o} portal={portal} />
        </QueryClientProvider>
      </StrictMode>,
    )
  }
  draw(message, opts).catch((err) => (opts.onError ? opts.onError(err as Error) : console.error('[pair-templates]', err)))
  return {
    update: (next, o) => draw(next, { ...current, ...o }),
    destroy: () => {
      root?.unmount()
      root = null
      shadow.innerHTML = ''
    },
  }
}

export { resolveCard, resolveMessage, LOCAL_TEMPLATES }
export type { Card, ResolveOptions, Message, MessageAction, ResolvedMessage, ActionResult }

// ---- <pair-template> ----
if (typeof window !== 'undefined' && window.customElements && !customElements.get('pair-template')) {
  class PairTemplateElement extends HTMLElement {
    static get observedAttributes() {
      return ['card', 'lang', 'theme', 'resolve-url']
    }
    private value: Card | null = null
    private handle: RenderHandle | null = null
    /**
     * The card, as an object or as its JSON. Vue and React 19 hand a custom element its
     * props as properties when the element has one by that name, so `card` is a real
     * property here, not only the attribute.
     */
    set card(card: Card | string | null) {
      if (typeof card === 'string') {
        try {
          card = JSON.parse(card) as Card
        } catch (error) {
          this.dispatchEvent(new CustomEvent('pair-template-error', { detail: error }))
          card = null
        }
      }
      this.value = card
      this.draw()
    }
    get card(): Card | null {
      return this.value
    }
    set message(card: Card | null) {
      this.card = card
    }
    get message() {
      return this.value
    }
    connectedCallback() {
      if (!this.style.display) this.style.display = 'block'
      this.draw()
    }
    disconnectedCallback() {
      this.handle?.destroy()
      this.handle = null
    }
    attributeChangedCallback() {
      this.draw()
    }
    private draw() {
      let card = this.value
      if (!card && this.getAttribute('card')) {
        try {
          card = JSON.parse(this.getAttribute('card') as string)
        } catch {
          return
        }
      }
      if (!card || !this.isConnected) return
      const opts: RenderOptions = {
        lang: (this.getAttribute('lang') as 'ar' | 'en' | null) ?? undefined,
        theme: (this.getAttribute('theme') as 'light' | 'dark' | null) ?? undefined,
        resolveUrl: this.getAttribute('resolve-url') ?? undefined,
        onError: (error) => this.dispatchEvent(new CustomEvent('pair-template-error', { detail: error })),
        onAction: (action) => actionEvent(this, 'pair-template-action', action),
      }
      if (this.handle) this.handle.update(card, opts).catch(() => {})
      else this.handle = render(this, card, opts)
    }
  }
  customElements.define('pair-template', PairTemplateElement)
}

// ---- <pair-message> ----
if (typeof window !== 'undefined' && window.customElements && !customElements.get('pair-message')) {
  /**
   * <pair-message lang="ar" theme="light" resolve-url="…"> — a whole reply. Set `.message` (an object, the
   * pieces array, or its JSON) or the `message` attribute (JSON). Fires `pair-message-action` when something is
   * tapped: {type: 'suggestion' | 'answer', text}, or {type: 'action', id, data, respond} for an action button (call
   * event.preventDefault(), run it, then detail.respond({success, message})); and `pair-template-error` for a card
   * that could not be drawn.
   */
  class PairMessageElement extends HTMLElement {
    static get observedAttributes() {
      return ['message', 'lang', 'theme', 'resolve-url']
    }
    private value: Message | null = null
    private handle: MessageHandle | null = null
    set message(m: Message | string | null) {
      if (typeof m === 'string') {
        try {
          m = JSON.parse(m) as Message
        } catch (error) {
          this.dispatchEvent(new CustomEvent('pair-template-error', { detail: error }))
          m = null
        }
      }
      this.value = m
      this.draw()
    }
    get message(): Message | null {
      return this.value
    }
    connectedCallback() {
      if (!this.style.display) this.style.display = 'block'
      this.draw()
    }
    disconnectedCallback() {
      this.handle?.destroy()
      this.handle = null
    }
    attributeChangedCallback() {
      this.draw()
    }
    private draw() {
      let m = this.value
      if (!m && this.getAttribute('message')) {
        try {
          m = JSON.parse(this.getAttribute('message') as string) as Message
        } catch {
          return
        }
      }
      if (!m || !this.isConnected) return
      const opts: MessageOptions = {
        lang: (this.getAttribute('lang') as 'ar' | 'en' | null) ?? undefined,
        theme: (this.getAttribute('theme') as 'light' | 'dark' | null) ?? undefined,
        resolveUrl: this.getAttribute('resolve-url') ?? undefined,
        onError: (error) => this.dispatchEvent(new CustomEvent('pair-template-error', { detail: error })),
        onAction: (action) => actionEvent(this, 'pair-message-action', action),
      }
      if (this.handle) this.handle.update(m, opts).catch(() => {})
      else this.handle = renderMessage(this, m, opts)
    }
  }
  customElements.define('pair-message', PairMessageElement)
}
